import app from 'flarum/forum/app';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import LogInModal from 'flarum/forum/components/LogInModal';

/**
 * STANDING REQUIREMENT, user decision 2026-09-04: this feature must remain
 * enabled going forward regardless of unrelated layout/hero/theme work in
 * this extension, until the user explicitly says otherwise. It was
 * accidentally left disabled for a real stretch after the SP-13 hero
 * rebuild (its call site got commented out to hold back an then-unreviewed
 * feature, and nothing surfaced that it had happened until the user tested
 * the flow directly) -- this file exists specifically so that class of
 * accident can't happen again: hero/nav/layout edits have no reason to ever
 * touch this file, so a diff limited to that kind of work will show zero
 * lines here. Do not inline this back into index.js.
 *
 * Before shipping ANY future build of this extension, verify `citregStart`
 * still appears in the compiled `js/dist/forum.js` (grep the compiled
 * output, not just this source file -- webpack silently tree-shakes this
 * entire module out with no warning if nothing calls startCitRegDiscussion).
 *
 * --- SP-3A pilot: "Start a discussion" from a Literature page. ---
 * Stopgap, not the eventually-approved soft-gated flow: an anonymous visitor
 * still hits Flarum's normal LogInModal (same behavior as core's own
 * IndexPage#newDiscussionAction) rather than drafting first. A logged-in
 * visitor gets the discussion composer pre-filled with a title, a link back
 * to the archive page, an embedded page image, and the ID/DS + Literature
 * tags pre-selected -- no new tag created. Triggered by a plain query string
 * (?citregStart=1&citregTitle=...&citregUrl=...&citregImage=...&citregTags=id-ds,literature)
 * so the Literature side only ever needs to build a link, never touch Forum internals.
 */
function citregDiscussionParams() {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('citregStart')) return null;

  return {
    title: params.get('citregTitle') || '',
    ref: params.get('citregRef') || '',
    url: params.get('citregUrl') || '',
    image: params.get('citregImage') || '',
    tags: (params.get('citregTags') || '').split(',').map((slug) => slug.trim()).filter(Boolean),
  };
}

// Factored out so its exact rendered length can be reused as the Tab-target
// cursor position below, without duplicating the string.
function citregReferenceLine(data) {
  return `reference: [${data.ref || 'this page'}](${data.url})`;
}

// The reference line itself is now the first line of the body -- the
// earlier separate "Write your message here..." instructional placeholder
// was dropped as redundant once this line existed anyway (two lines saying
// "there's something here to replace/reference" was one too many). The
// cursor is still moved to position 0 below, so typing naturally happens
// before this line rather than requiring it to be selected/deleted first.
// Three trailing spaces after the reference line double as the Tab-target
// landing spot (see startCitRegDiscussion) -- note for anyone touching this
// later: 2+ trailing spaces is the standard Markdown "forced line break"
// convention, so these render as a break, not just visual padding; that's
// an accepted side effect here, not an oversight.
function citregComposerBody(data) {
  const lines = [];
  if (data.url) lines.push(citregReferenceLine(data) + '   ', '', '---', '');
  if (data.image) {
    const imageMarkdown = `![${data.title || 'page'}](${data.image})`;
    // Wrapping the image in the same page link makes the image itself
    // clickable back to the source, not just the text reference above --
    // standard Markdown image-inside-link nesting, no special handling
    // needed from the formatter.
    lines.push(data.url ? `[${imageMarkdown}](${data.url})` : imageMarkdown);
  }
  return lines.join('\n');
}

export default function startCitRegDiscussion() {
  const data = citregDiscussionParams();
  if (!data) return;

  if (!app.session.user) {
    // Deliberately NOT stripping the query string here. LogInModal's own
    // success handler does an unconditional window.location.reload()
    // (confirmed directly in its source) -- if the query string were
    // already gone by then, this whole flow would have nothing to resume
    // with after that reload, landing on the plain homepage instead of the
    // pre-filled composer. Leaving it intact means this same function runs
    // again on the fresh post-login page load and picks up right where it
    // left off.
    app.modal.show(LogInModal);
    return;
  }

  // Only clear it now, once we're actually about to consume it -- a reload
  // before this point (e.g. during login) must not lose the pending request.
  window.history.replaceState(null, '', window.location.pathname);

  app.store.find('tags').then((allTags) => {
    const matchedTags = allTags.filter((tag) => data.tags.includes(tag.slug()));

    // Title left genuinely empty -- a real native placeholder prompts an
    // actual topic sentence instead of auto-filling a citation-shaped
    // string into it. The permanent reference link in the body (above)
    // already anchors this discussion to the source page.
    app.composer.load(DiscussionComposer, {
      user: app.session.user,
      originalContent: citregComposerBody(data),
      titlePlaceholder: 'Topic:',
    });
    app.composer.show();
    app.composer.fields.tags = matchedTags;
    m.redraw();

    // The editor driver is only attached once TextEditor's own oncreate runs
    // after this redraw actually mounts the DOM -- defer to the next tick
    // rather than assuming app.composer.editor already exists here.
    window.setTimeout(() => {
      if (app.composer.editor) app.composer.editor.moveCursorTo(0);
    }, 0);
  });
}
